import Ionicons from "@expo/vector-icons/Ionicons";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import type { VenueCandidate } from "@/api";
import { publicConfig } from "@/config/public";
import { notify } from "@/lib/dialog";
import { locationStrings } from "@/i18n/features/location.i18n";
import { getCurrentCoords, type Coords } from "@/lib/location";
import { useSessionStore, visibilityRefusalReason } from "@/state/session-store";
import { useAppTheme } from "@/theme/app-theme";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

// Two different bounds, deliberately:
//
//  SEARCH_RADIUS_METERS — how far out a place may be *shown*. Wider, so a user
//    can see the café across the road and know it exists.
//  JOIN_RADIUS_METERS   — how close they must be to *check in*. The server is
//    authoritative and re-checks this; the client uses it only to disable the
//    row and explain why.
//
// Both come from environments/<env>/public.yaml while the server reads
// server.yaml. If those disagree the smaller wins and results silently vanish,
// so keep them in step.
const METERS_PER_MILE = 1609.344;
const SEARCH_RADIUS_METERS = publicConfig.location.venueSearchRadiusMeters;
// The DISTANCE bound, not `venueJoinRadiusMeters`. The two are different
// numbers now: that one bounds how accurate the fix must be (and feeds the
// server's accuracy+drift budget), this one is how far the space may be.
// Filtering on the wrong one would offer rows the server then refuses with
// "move closer" — the client and server holding two rules about one distance.
const JOIN_RADIUS_METERS = publicConfig.location.venueJoinDistanceMeters;
// Absorbs float/rounding differences between the server's great-circle maths
// and ours, so a candidate sitting exactly on the boundary is not dropped.
const RADIUS_TOLERANCE_METERS = 1;
const MAX_SUGGESTIONS = 6;
const SEARCH_DEBOUNCE_MS = 280;
const SEARCH_MIN_CHARS = 3;

type LocationStatus = "idle" | "locating" | "ready" | "unavailable";

type VenueSuggestion = VenueCandidate & {
  distanceMeters: number;
};

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

/**
 * Bare geometry. A venue's published coordinates are not a device reading, so
 * they carry no accuracy — only `Coords` does, and only where the distance is
 * being used to decide something.
 */
type LatLng = { lat: number; lng: number };

function distanceMeters(a: LatLng, b: LatLng): number {
  const earthRadius = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

function makeSuggestions(
  candidates: VenueCandidate[],
  origin: Coords | null,
): VenueSuggestion[] {
  // A GPS fix is required because this correction flow must never offer a
  // venue outside the permitted radius around the user.
  if (!origin) return [];

  const unique = new Map<string, VenueSuggestion>();

  for (const candidate of candidates) {
    if (
      typeof candidate.lat !== "number" ||
      typeof candidate.lng !== "number" ||
      !Number.isFinite(candidate.lat) ||
      !Number.isFinite(candidate.lng)
    ) {
      continue;
    }

    const distance = distanceMeters(origin, {
      lat: candidate.lat,
      lng: candidate.lng,
    });

    // Show anything inside the search radius. Whether it can actually be
    // joined is decided per row via JOIN_RADIUS_METERS — a place you can see
    // but not yet check in to is useful information, not noise.
    if (distance > SEARCH_RADIUS_METERS + RADIUS_TOLERANCE_METERS) continue;

    const existing = unique.get(candidate.placeId);

    if (!existing || distance < existing.distanceMeters) {
      unique.set(candidate.placeId, {
        ...candidate,
        distanceMeters: distance,
      });
    }
  }

  // Area context sorts last: it is a fallback label, not a choice competing
  // with real venues.
  return [...unique.values()].sort((a, b) => {
    const aArea = a.kind === "area" ? 1 : 0;
    const bArea = b.kind === "area" ? 1 : 0;
    if (aArea !== bArea) return aArea - bArea;
    return a.distanceMeters - b.distanceMeters;
  });
}

function matchesVenuePrefix(candidate: VenueCandidate, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;

  const name = candidate.name.trim().toLocaleLowerCase();
  if (name.startsWith(normalized)) return true;

  return name
    .split(/[\s,.'’()-]+/)
    .filter(Boolean)
    .some((part) => part.startsWith(normalized));
}

function formatDistance(
  distance: number,
  locale: string,
  t: TFunction,
): string {
  if (distance < 1000) {
    const value = new Intl.NumberFormat(locale).format(
      Math.max(10, Math.round(distance / 10) * 10),
    );
    return t(locationStrings.metersAway, { distance: value });
  }

  const miles = distance / METERS_PER_MILE;
  const value = new Intl.NumberFormat(locale, {
    maximumFractionDigits: miles < 10 ? 1 : 0,
  }).format(miles);
  return t(locationStrings.milesAway, { distance: value });
}

/**
 * A bound, as the user should read it. The search radius is a round kilometre
 * now, and "1000 metres" reads like a machine talking — say "1 km" instead. Only
 * exact multiples convert, so an odd bound like 1500 stays in metres rather than
 * becoming a "1.5 km" that implies more precision than these bounds carry.
 */
function radiusLabel(meters: number, t: TFunction): string {
  if (meters >= 1000 && meters % 1000 === 0) {
    return t(locationStrings.radiusKilometres, { count: meters / 1000 });
  }
  return t(locationStrings.radiusMeters, { count: meters });
}

function isJoinDistanceError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /within \d+ meters of your current GPS location/i.test(error.message)
  );
}

export function EditLocationEditor({
  visible,
  currentVenueName,
  onClose,
  onUpdated,
}: {
  visible: boolean;
  /**
   * The space the user has already JOINED, if any — never the resolver's
   * guess at where they are.
   *
   * It marks one row "Current", and the word has to be earned. Handed the
   * guess, this screen badged whichever business the heartbeat last resolved
   * to as the user's space, contradicting the space they had actually chosen.
   * Undefined when there is no participation, so nothing claims to be current,
   * which is the honest answer.
   *
   * Picking the current row is still a real action, unlike the plain location
   * edit this screen used to double as: the whole flow exists so somebody can
   * NAME the room they are in, and confirming the one already shown is the
   * most natural thing they can do.
   */
  currentVenueName?: string;
  onClose: () => void;
  onUpdated?: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { isDark } = useAppTheme();
  const colors = useMemo(() => getEditorColors(isDark), [isDark]);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  const searchVenues = useSessionStore((state) => state.searchVenues);
  const nearbyVenues = useSessionStore((state) => state.nearbyVenues);
  const joinVenue = useSessionStore((state) => state.joinVenue);

  const [query, setQuery] = useState("");
  const [nearbyPool, setNearbyPool] = useState<VenueSuggestion[]>([]);
  const [results, setResults] = useState<VenueSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("idle");
  const [searchFocused, setSearchFocused] = useState(false);

  const coordsRef = useRef<Coords | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const loadNearby = useCallback(
    async (origin: Coords) => {
      const requestId = ++requestIdRef.current;
      setSearching(true);

      try {
        const found = await nearbyVenues(origin);
        if (requestId !== requestIdRef.current) return;

        const suggestions = makeSuggestions(found, origin);
        setNearbyPool(suggestions);
        setResults(suggestions);
      } catch {
        if (requestId === requestIdRef.current) {
          setNearbyPool([]);
          setResults([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setSearching(false);
        }
      }
    },
    [nearbyVenues],
  );

  const searchNearby = useCallback(
    async (value: string) => {
      const origin = coordsRef.current;
      if (!origin) return;

      const requestId = ++requestIdRef.current;
      setSearching(true);

      try {
        const found = await searchVenues(value, origin);
        if (requestId !== requestIdRef.current) return;

        setResults(
          makeSuggestions(found, origin).filter((candidate) =>
            matchesVenuePrefix(candidate, value),
          ),
        );
      } catch {
        if (requestId === requestIdRef.current) {
          setResults([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setSearching(false);
        }
      }
    },
    [searchVenues],
  );

  const resolveLocationAndLoad = useCallback(async () => {
    requestIdRef.current += 1;
    setLocationStatus("locating");
    setSearching(true);
    setNearbyPool([]);
    setResults([]);

    const locationResult = await getCurrentCoords();

    if (locationResult.status !== "ok") {
      coordsRef.current = null;
      setLocationStatus("unavailable");
      setSearching(false);
      return;
    }

    coordsRef.current = locationResult.coords;
    setLocationStatus("ready");
    await loadNearby(locationResult.coords);
  }, [loadNearby]);

  useEffect(() => {
    if (!visible) {
      requestIdRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    setQuery("");
    setJoiningId(null);
    setSearchFocused(false);
    void resolveLocationAndLoad();

    return () => {
      requestIdRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, resolveLocationAndLoad]);

  useEffect(() => {
    if (!visible || locationStatus !== "ready") return;

    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = query.trim();

    if (trimmed.length === 0) {
      requestIdRef.current += 1;
      setSearching(false);
      setResults(nearbyPool);
      return;
    }

    if (trimmed.length < SEARCH_MIN_CHARS) {
      setSearching(false);
      setResults([]);
      return;
    }

    // Give useful prefix feedback from the already-loaded local pool before
    // the remote search returns.
    const localMatches = nearbyPool.filter((candidate) =>
      matchesVenuePrefix(candidate, trimmed),
    );

    setResults(localMatches);

    timerRef.current = setTimeout(() => {
      void searchNearby(trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, query, nearbyPool, locationStatus, searchNearby]);

  // Two labels, because the two bounds are user-visible in different places.
  // "Choose a venue within X" is the join rule; "results beyond X stay hidden"
  // is the search rule. Using one for both told users results stop at 200m
  // while a 300m result sat on screen — and the two are five times apart now,
  // so conflating them would be five times as confusing.
  const joinRadiusLabel = radiusLabel(JOIN_RADIUS_METERS, t);
  const searchRadiusLabel = radiusLabel(SEARCH_RADIUS_METERS, t);

  function isTooFarToJoin(candidate: VenueSuggestion): boolean {
    return (
      candidate.distanceMeters > JOIN_RADIUS_METERS + RADIUS_TOLERANCE_METERS
    );
  }

  function notifyTooFar(candidate: VenueSuggestion) {
    notify(
      t(locationStrings.tooFarTitle),
      t(locationStrings.tooFarBody, {
        name: candidate.name,
        radius: joinRadiusLabel,
      }),
    );
  }

  async function onPick(candidate: VenueSuggestion) {
    // Area rows are street/locality context, never a space to check in to.
    // The API rejects them too; this just avoids a pointless round trip.
    if (candidate.kind === "area") return;

    if (isTooFarToJoin(candidate)) {
      notifyTooFar(candidate);
      return;
    }

    /**
     * Every row joins, including the one badged "Current". No short-circuit.
     *
     * There was one, and it had to go with the meaning of `currentVenueName`.
     * While that prop named the venue the RESOLVER had checked the user into,
     * skipping the join was right: they were already there, and the caller's
     * `onUpdated` had the same venue in the store to act on.
     *
     * It names the JOINED space now, which the heartbeat does not track — so
     * the store's `venue` can be the business next door. Skipping the join
     * would then leave `onUpdated` reading that neighbour and opting the user
     * into it: re-confirming Kia would have quietly moved them to Powercity.
     *
     * Going through the join costs one request and re-proves proximity, which
     * is what makes re-picking a space you have walked away from fail with a
     * reason instead of succeeding on a stale claim.
     */
    setJoiningId(candidate.placeId);

    try {
      const origin = coordsRef.current;
      if (!origin) {
        throw new Error("GPS is required to refine your current space");
      }
      await joinVenue(candidate, origin);
      await onUpdated?.();
      onClose();
    } catch (error) {
      if (isJoinDistanceError(error)) {
        notifyTooFar(candidate);
      } else if (visibilityRefusalReason(error)) {
        // Not a failure the user needs a sentence about, and the earlier
        // version that gave them one was solving the wrong problem. The venue
        // change SUCCEEDED — `joinVenue` writes it before attempting to publish
        // — and a stale fix is then re-taken automatically by the recovery in
        // `session-store.ts`. An imprecise one surfaces on the home screen,
        // which is where the remedies (a precise grant, a wider range) live.
        //
        // So this closes like any other success. A toast here would interrupt a
        // correction that worked to report a condition the app is already
        // handling, and neither of its two messages had an action attached.
        onClose();
      } else {
        notify(
          t(locationStrings.updateFailed),
          t(locationStrings.updateFailedBody),
        );
      }
    } finally {
      setJoiningId(null);
    }
  }

  if (!visible) return null;

  const visibleResults = results.slice(0, MAX_SUGGESTIONS);
  const isLocating = locationStatus === "locating";
  const locationUnavailable = locationStatus === "unavailable";

  return (
    <View style={styles.editor}>
      <View style={styles.editorHeader}>
        <View style={styles.editorHeading}>
          <View style={styles.editorIcon}>
            <Ionicons name="locate-outline" size={18} color={colors.primary} />
          </View>

          <View style={styles.editorCopy}>
            <Text style={styles.editorTitle}>
              {t(locationStrings.editorTitle)}
            </Text>
            <Text style={styles.editorSubtitle}>
              {t(locationStrings.editorBody, { radius: joinRadiusLabel })}
            </Text>
          </View>
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(locationStrings.editorCloseA11y)}
          hitSlop={10}
          onPress={onClose}
          style={({ pressed }) => [
            styles.closeButton,
            pressed && styles.closeButtonPressed,
          ]}
        >
          <Ionicons name="close" size={19} color={colors.iconNeutral} />
        </Pressable>
      </View>

      <View
        style={[styles.searchBox, searchFocused && styles.searchBoxFocused]}
      >
        <View style={styles.searchIconWrap}>
          {searching || isLocating ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <Ionicons
              name="search-outline"
              size={19}
              color={colors.iconNeutral}
            />
          )}
        </View>

        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={t(locationStrings.editorPlaceholder)}
          placeholderTextColor={colors.textMuted}
          style={[styles.searchInput, styles.searchInputWebReset]}
          autoFocus
          autoCorrect={false}
          autoCapitalize="words"
          returnKeyType="search"
          editable={!locationUnavailable && !isLocating}
          onSubmitEditing={() => {
            const trimmed = query.trim();
            if (trimmed.length >= SEARCH_MIN_CHARS) void searchNearby(trimmed);
          }}
        />

        {query.length > 0 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(locationStrings.editorClearA11y)}
            hitSlop={8}
            onPress={() => setQuery("")}
            style={styles.clearButton}
          >
            <Ionicons name="close-circle" size={19} color={colors.textMuted} />
          </Pressable>
        ) : null}
      </View>

      {locationUnavailable ? (
        <View style={styles.locationError}>
          <View style={styles.locationErrorIcon}>
            <Ionicons name="location-outline" size={20} color={colors.danger} />
          </View>

          <View style={styles.locationErrorCopy}>
            <Text style={styles.locationErrorTitle}>
              {t(locationStrings.gpsUnavailable)}
            </Text>
            <Text style={styles.locationErrorText}>
              {t(locationStrings.gpsRequired, { radius: joinRadiusLabel })}
            </Text>
          </View>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(locationStrings.retryGpsA11y)}
            onPress={() => void resolveLocationAndLoad()}
            style={({ pressed }) => [
              styles.retryButton,
              pressed && styles.retryButtonPressed,
            ]}
          >
            <Ionicons name="refresh" size={17} color={colors.primary} />
          </Pressable>
        </View>
      ) : (
        <View style={styles.dropdown}>
          <View style={styles.dropdownHeader}>
            <Text style={styles.dropdownTitle}>
              {t(
                query.trim().length > 0
                  ? locationStrings.matchingPlaces
                  : locationStrings.closestPlaces,
              )}
            </Text>

            {!searching && visibleResults.length > 0 ? (
              <Text style={styles.dropdownCount}>
                {t(locationStrings.suggestionCount, {
                  count: visibleResults.length,
                })}
              </Text>
            ) : null}
          </View>

          {visibleResults.map((item, index) => {
            const joining = joiningId === item.placeId;
            const isArea = item.kind === "area";
            // Visible up to the search radius, joinable only inside the join
            // radius. The server enforces the same bound and would reject it.
            const tooFarToJoin = isTooFarToJoin(item);
            const current =
              !isArea &&
              currentVenueName?.trim().toLocaleLowerCase() ===
                item.name.trim().toLocaleLowerCase();
            const blocked =
              joiningId !== null || isArea;
            const accessibilityLabel = isArea
              ? t(locationStrings.areaContextA11y, { name: item.name })
              : current
                ? t(locationStrings.currentLocationA11y, {
                    name: item.name,
                  })
                : tooFarToJoin
                  ? t(locationStrings.tooFarA11y, {
                      name: item.name,
                      radius: joinRadiusLabel,
                    })
                  : t(locationStrings.setLocationA11y, { name: item.name });

            return (
              <View key={item.placeId}>
                {index > 0 ? <View style={styles.separator} /> : null}

                <Pressable
                  accessibilityRole={isArea ? "text" : "button"}
                  accessibilityLabel={accessibilityLabel}
                  accessibilityState={{ disabled: blocked }}
                  disabled={blocked}
                  onPress={() => void onPick(item)}
                  style={({ pressed }) => [
                    styles.resultRow,
                    pressed && !blocked && styles.resultRowPressed,
                    current && styles.currentResultRow,
                    joiningId !== null && !joining && styles.resultRowMuted,
                  ]}
                >
                  <View
                    style={[
                      styles.resultIcon,
                      current && styles.currentResultIcon,
                    ]}
                  >
                    <Ionicons
                      name={current ? "checkmark" : "location-outline"}
                      size={19}
                      color={current ? colors.white : colors.primary}
                    />
                  </View>

                  <View style={styles.resultCopy}>
                    <View style={styles.resultNameRow}>
                      <Text style={styles.resultName} numberOfLines={1}>
                        {item.name}
                      </Text>

                      {current ? (
                        <View style={styles.currentBadge}>
                          <Text style={styles.currentBadgeText}>
                            {t(locationStrings.current)}
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    <Text style={styles.resultMeta} numberOfLines={1}>
                      {[item.category, item.address]
                        .filter(Boolean)
                        .join(" · ") || t(locationStrings.nearbyVenue)}
                    </Text>
                  </View>

                  <View style={styles.resultRight}>
                    <Text style={styles.distanceText}>
                      {formatDistance(item.distanceMeters, i18n.language, t)}
                    </Text>

                    {joining ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : !current ? (
                      <Ionicons
                        name="chevron-forward"
                        size={17}
                        color={colors.textMuted}
                      />
                    ) : null}
                  </View>
                </Pressable>
              </View>
            );
          })}

          {searching && visibleResults.length === 0 ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={styles.emptyText}>
                {t(locationStrings.findingMatches)}
              </Text>
            </View>
          ) : null}

          {!searching && visibleResults.length === 0 ? (
            <View style={styles.emptyState}>
              <View style={styles.emptyCopy}>
                <Text style={styles.emptyTitle}>
                  {t(locationStrings.noMatch)}
                </Text>
                <Text style={styles.emptyText}>
                  {query.trim().length > 0 &&
                  query.trim().length < SEARCH_MIN_CHARS
                    ? t(locationStrings.minChars, { count: SEARCH_MIN_CHARS })
                    : query.trim().length > 0
                      ? t(locationStrings.tryName, {
                          radius: searchRadiusLabel,
                        })
                      : t(locationStrings.noneInRadius, {
                          radius: searchRadiusLabel,
                        })}
                </Text>
              </View>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
}

function getEditorColors(isDark: boolean) {
  return {
    surface: isDark ? "#152235" : "#FFFFFF",
    surfaceMuted: isDark ? "#19283B" : "#F6F9FD",
    surfacePressed: isDark ? "#213650" : "#F2F7FF",
    border: isDark ? "#2C3C51" : "#DFE7F1",
    borderStrong: isDark ? "#3B5E88" : "#90B5ED",
    divider: isDark ? "#2A3A4E" : "#E8EDF4",
    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textMuted: isDark ? "#8999AD" : "#8896A8",
    iconNeutral: isDark ? "#A9B9CD" : "#526C8D",
    primary: isDark ? "#6EA8FF" : "#2468D8",
    primarySoft: isDark ? "#1D314B" : "#EEF4FF",
    primaryBorder: isDark ? "#34577F" : "#D7E5FA",
    danger: isDark ? "#FF929B" : "#B54A57",
    dangerSoft: isDark ? "#3A222B" : "#FFF3F4",
    shadow: isDark ? "#000000" : "#7187A6",
    white: "#FFFFFF",
  };
}

type EditorColors = ReturnType<typeof getEditorColors>;

function createStyles(colors: EditorColors, isDark: boolean) {
  return StyleSheet.create({
    editor: {
      position: "relative",
      padding: 16,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.24 : 0.11,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 8 },
      elevation: 4,
    },
    connector: {
      position: "absolute",
      top: -7,
      right: 34,
      width: 14,
      height: 14,
      transform: [{ rotate: "45deg" }],
      borderLeftWidth: 1,
      borderTopWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    editorHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 13,
    },
    editorHeading: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },
    editorIcon: {
      width: 38,
      height: 38,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      backgroundColor: colors.primarySoft,
    },
    editorCopy: {
      flex: 1,
    },
    editorTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 20,
      fontWeight: "700",
    },
    editorSubtitle: {
      marginTop: 2,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },
    closeButton: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.surfaceMuted,
    },
    closeButtonPressed: {
      opacity: 0.7,
      transform: [{ scale: 0.96 }],
    },
    searchBox: {
      minHeight: 54,
      paddingHorizontal: 11,
      flexDirection: "row",
      alignItems: "center",
      gap: 9,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.16 : 0.06,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 3 },
      elevation: 1,
    },
    searchBoxFocused: {
      borderColor: colors.primary,
      shadowColor: colors.primary,
      shadowOpacity: 0.18,
    },
    searchIconWrap: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
      backgroundColor: colors.primarySoft,
    },
    searchInput: {
      flex: 1,
      minHeight: 52,
      paddingVertical: 0,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "500",
    },
    searchInputWebReset:
      Platform.OS === "web"
        ? ({ outlineStyle: "none" } as object)
        : {},
    clearButton: {
      width: 30,
      height: 30,
      alignItems: "center",
      justifyContent: "center",
    },
    radiusNotice: {
      minHeight: 32,
      paddingHorizontal: 3,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    radiusNoticeText: {
      flex: 1,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 10.5,
      lineHeight: 15,
    },
    // Deliberately flat. The results used to sit in their own bordered,
    // filled panel inside the editor's panel, inside the screen's card — three
    // nested surfaces for one list. The header now reads as a section label
    // over rows that belong to the editor, not as the chrome of a second box.
    dropdown: {
      marginTop: 4,
    },
    dropdownHeader: {
      minHeight: 34,
      paddingHorizontal: 3,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
    },
    dropdownTitle: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    dropdownCount: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      fontWeight: "600",
    },
    separator: {
      height: StyleSheet.hairlineWidth,
      // Starts where the row's text starts: 3 (padding) + 36 (icon) + 10 (gap).
      marginLeft: 49,
      backgroundColor: colors.divider,
    },
    resultRow: {
      minHeight: 68,
      paddingHorizontal: 3,
      paddingVertical: 9,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: 12,
    },
    resultRowPressed: {
      backgroundColor: colors.surfacePressed,
    },
    currentResultRow: {
      backgroundColor: colors.primarySoft,
    },
    resultRowMuted: {
      opacity: 0.5,
    },
    resultIcon: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.primarySoft,
    },
    currentResultIcon: {
      backgroundColor: colors.primary,
    },
    resultCopy: {
      flex: 1,
      minWidth: 0,
    },
    resultNameRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
    },
    resultName: {
      flexShrink: 1,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 13.5,
      lineHeight: 18,
      fontWeight: "700",
    },
    currentBadge: {
      minHeight: 20,
      paddingHorizontal: 7,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 7,
      backgroundColor: colors.surface,
    },
    currentBadgeText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 8.5,
      fontWeight: "800",
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    resultMeta: {
      marginTop: 2,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 10.5,
      lineHeight: 15,
    },
    resultRight: {
      maxWidth: 86,
      alignItems: "flex-end",
      justifyContent: "center",
      gap: 4,
    },
    distanceText: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 9.5,
      lineHeight: 13,
      textAlign: "right",
    },
    emptyState: {
      minHeight: 62,
      paddingHorizontal: 3,
      paddingVertical: 13,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },
    emptyCopy: {
      flex: 1,
    },
    emptyTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },
    emptyText: {
      flexShrink: 1,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },
    locationError: {
      minHeight: 78,
      paddingHorizontal: 12,
      paddingVertical: 11,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.danger,
      backgroundColor: colors.dangerSoft,
    },
    locationErrorIcon: {
      width: 38,
      height: 38,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.surface,
    },
    locationErrorCopy: {
      flex: 1,
    },
    locationErrorTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },
    locationErrorText: {
      marginTop: 2,
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 10.5,
      lineHeight: 15,
    },
    retryButton: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.surface,
    },
    retryButtonPressed: {
      opacity: 0.7,
      transform: [{ rotate: "15deg" }],
    },
  });
}
